/**
 * Analytics — GA4 (optional) + Internal Visitor Tracker (always-on).
 * GA4 loads only if REACT_APP_GA4_MEASUREMENT_ID is set.
 * Internal tracker always posts to /api/track/pageview for super-admin visibility.
 */
const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

let initialized = false;

export const initGA = () => {
  if (initialized || !GA_ID || typeof window === "undefined") return;
  initialized = true;
  // Inject GA script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
};

// Internal visitor tracker — siempre activo, sin terceros, privacy-first
const _trackerState = {
  currentViewId: null,
  startTime: 0,
  maxScrollPct: 0,
  currentPath: null,
};

const _computeScrollPct = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return 0;
  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
  const docHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body ? document.body.scrollHeight : 0
  );
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const denom = Math.max(1, docHeight - viewportHeight);
  return Math.min(100, Math.round(((scrollTop + viewportHeight) / docHeight) * 100));
};

const _onScroll = () => {
  const pct = _computeScrollPct();
  if (pct > _trackerState.maxScrollPct) _trackerState.maxScrollPct = pct;
};

const _finalizeView = () => {
  if (!_trackerState.currentViewId || !BACKEND_URL) return;
  const elapsed = Math.max(0, Date.now() - _trackerState.startTime);
  const payload = {
    view_id: _trackerState.currentViewId,
    time_on_page_ms: elapsed,
    max_scroll_pct: _trackerState.maxScrollPct,
  };
  try {
    // sendBeacon es ideal para beforeunload; cae a fetch keepalive si no
    const url = `${BACKEND_URL}/api/track/pageview/complete`;
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) { /* fail silently */ }
  _trackerState.currentViewId = null;
};

let _listenersBound = false;
const _bindLifecycleListeners = () => {
  if (_listenersBound || typeof window === "undefined") return;
  _listenersBound = true;
  window.addEventListener("scroll", _onScroll, { passive: true });
  window.addEventListener("beforeunload", _finalizeView);
  window.addEventListener("pagehide", _finalizeView);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") _finalizeView();
  });
};

const _postInternalPageView = async (path, title) => {
  if (typeof window === "undefined" || !BACKEND_URL) return;
  try {
    // Si veníamos de otra vista, ciérrala primero
    if (_trackerState.currentViewId && _trackerState.currentPath !== path) {
      _finalizeView();
    }
    _bindLifecycleListeners();
    const resp = await fetch(`${BACKEND_URL}/api/track/pageview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: path || window.location.pathname,
        referrer: document.referrer || "",
        title: title || document.title || "",
        screen_width: window.innerWidth || 0,
      }),
      keepalive: true,
    });
    if (resp.ok) {
      const data = await resp.json().catch(() => ({}));
      if (data && data.view_id) {
        _trackerState.currentViewId = data.view_id;
        _trackerState.startTime = Date.now();
        _trackerState.maxScrollPct = _computeScrollPct();
        _trackerState.currentPath = path;
      }
    }
  } catch (e) { /* fail silently */ }
};

const _postInternalEvent = (event_type, metadata = {}) => {
  if (typeof window === "undefined" || !BACKEND_URL) return;
  try {
    fetch(`${BACKEND_URL}/api/track/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type,
        path: window.location.pathname,
        metadata,
      }),
      keepalive: true,
    }).catch(() => { /* fail silently */ });
  } catch (e) { /* fail silently */ }
};

export const trackPageView = (path, title) => {
  // Internal tracker (always)
  _postInternalPageView(path, title);
  // GA4 (optional)
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
};

export const trackEvent = (name, params = {}) => {
  // Internal tracker (always)
  _postInternalEvent(name, params);
  // GA4 (optional)
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
};

// Convenience helpers for the most common conversion events
export const trackLeadCapture = (source) => trackEvent("lead_capture", { source });
export const trackDemoStart = (industry) => trackEvent("demo_start", { industry });
export const trackOfficerQuery = (officer_id, context_type) =>
  trackEvent("officer_query", { officer_id, context_type });
export const trackSkillRun = (skill_id, tool) => trackEvent("skill_run", { skill_id, tool });
export const trackSignup = (method) => trackEvent("sign_up", { method });

// Officer Dock — granular UX funnel events
export const trackOfficerHint = (action) => trackEvent("officer_hint", { action }); // shown|clicked|dismissed
export const trackOfficerDockOpen = (context_type) => trackEvent("officer_dock_open", { context_type });
export const trackOfficerSelected = (officer_id) => trackEvent("officer_selected", { officer_id });
export const trackCouncilRun = (context_type) => trackEvent("council_run", { context_type });

export default { initGA, trackPageView, trackEvent, trackLeadCapture, trackDemoStart, trackOfficerQuery, trackSkillRun, trackSignup, trackOfficerHint, trackOfficerDockOpen, trackOfficerSelected, trackCouncilRun };
